//A col/row cell on the systems particle grid (zone is attached by getSpawnTarget):
export interface GridPoint {
	col: number;
	row: number;
	zone?: number;
}

export default {
	pointConfig: null,
	totalCols: null,
	totalRows: null,
	layout: 'landscape',
	zoneMatrix: [
		{ x: 1, y: 1 }, //Top Left
		{ x: 2, y: 1 }, //Top Middle
		{ x: 3, y: 1 }, //Top Right
		{ x: 1, y: 2 }, //Middle Left
		{ x: 2, y: 2 }, //Middle Middle
		{ x: 3, y: 2 }, //Middle Right
		{ x: 1, y: 3 }, //Bottom Left
		{ x: 2, y: 3 }, //Bottom Middle
		{ x: 3, y: 3 } //Bottom Right
	],
	spawnHistoryLength: 7, //Retain x4
	lastZoneSpawns: [], //

	setPointConfig(pointConfig) {
		this.pointConfig = pointConfig;
	},

	//Get a 45 degree target
	//If can't find move onto next direction
	get45DegreeTarget(startCol, startRow, minSteps, colDirection = null, rowDirection = null): GridPoint | null {
		//Try and get the next
		if (colDirection === null) {
			colDirection = Math.random() < 0.5 ? 1 : -1;
		}

		if (rowDirection === null) {
			rowDirection = Math.random() < 0.5 ? 1 : -1;
		}

		let target = null;
		let passes = 0;

		while (target === null && passes <= 4) {
			target = this.getTarget(startCol, startRow, minSteps, colDirection, rowDirection);

			passes++;

			if (passes == 1) colDirection *= -1;
			if (passes == 2) rowDirection *= -1;
			if (passes == 3) colDirection *= -1;
		}

		return target;
	},

	getRightAngleTarget(startCol, startRow, minSteps, colDirection = null, rowDirection = null): GridPoint | null {
		//Try and get the next
		let mode;
		if (colDirection === null && rowDirection === null) {
			if (Math.random() < 0.5) {
				colDirection = Math.random() < 0.5 ? 1 : -1;
				mode = 'v';
			} else {
				rowDirection = Math.random() < 0.5 ? 1 : -1;
				mode = 'h';
			}
		}

		let target = null;
		let passes = 0;

		while (target === null && passes <= 4) {
			target = this.getTarget(startCol, startRow, minSteps, colDirection, rowDirection);

			passes++;

			if (mode == 'h') {
				if (passes == 1) {
					colDirection *= -1;
				} else if (passes == 2) {
					colDirection = 0;
					rowDirection = Math.random() < 0.5 ? 1 : -1;
				} else if (passes == 3) {
					rowDirection *= -1;
				}
			} else {
				if (passes == 1) {
					rowDirection *= -1;
				} else if (passes == 2) {
					rowDirection = 0;
					colDirection = Math.random() < 0.5 ? 1 : -1;
				} else if (passes == 3) {
					colDirection *= -1;
				}
			}
		}

		return target;
	},

	getSpawnTarget(zones: Array<number> = null, forceCol = null, forceRow = null): GridPoint | null {
		//Split the grid into a matrix of 3x3 as per
		// 1 - 2 - 3
		// 4 - 5 - 6
		// 7 - 8 - 9

		if (!zones) {
			zones = [];
			let checkZones = [1, 2, 3, 4, 6, 7, 8, 9];
			checkZones.forEach((zone) => {
				if (!this.lastZoneSpawns.includes(zone)) {
					zones.push(zone);
				}
			});
		}

		//If landscape then rougly exclude the UI safe zone:
		if (this.layout != 'landscape' && !this.lastZoneSpawns.includes(5)) {
			zones.push(5);
		}

		//Pick a random zone

		let zone = zones[Math.floor(Math.random() * (zones.length - 0.01))];
		let zoneConfig = this.zoneMatrix[zone - 1];
		let zoneCols = Math.round(this.totalCols / 3);
		let zoneRows = Math.round(this.totalRows / 3);
		let baseX = (zoneConfig.x - 1) * zoneCols;
		let baseY = (zoneConfig.y - 1) * zoneRows;


		//Now decide where to try and put the target
		//Just randomise between the cols/rows in the
		let targetCol = forceCol ? forceCol : baseX + Math.round(Math.random() * zoneCols);
		let targetRow = forceRow ? forceRow : baseY + Math.round(Math.random() * zoneRows);


		//If there is point there already then just return
		if (this.hasPoint(targetCol, targetRow)) {
			this.addZoneHistory(zone);
			return {
				col: targetCol,
				row: targetRow,
				zone: zone
			};
		}

		//No point at random selection try and find nearest neighbour:
		//When doing this just to top-middle
		let point = this.getNearestHeighbour(targetCol, targetRow);

		if (point) {
			point.zone = zone;
			this.addZoneHistory(zone);
		}

		return point;
	},

	addZoneHistory(zone) {
		this.lastZoneSpawns.push(zone);

		if (this.lastZoneSpawns.length > this.spawnHistoryLength) {
			this.lastZoneSpawns.shift();
		}
	},

	hasPoint(col, row) {
		return this.pointConfig[col] && this.pointConfig[col][row];
	},

	getNearestHeighbour(col, row, passes = 5): GridPoint | null {
		//Expand out from the col/row and find a nearest neighbout
		let jump = 1;

		for (let i = 0; i < passes; i++) {
			let x = jump;
			let y = 0;

			//Right
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Left
			x *= -1;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Bottom Left
			y = jump;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Top Left
			y *= -1;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Top Right
			x *= -1;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Bottom Right
			y *= -1;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Bottom
			x = 0;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			//Top
			y *= -1;
			if (this.hasPoint(col + x, row + y)) return { col: col + x, row: row + y };

			jump++;
		}

		return null;
	},

	//Get a target on one vector
	//Will work at 45, Vertical or Horizontal
	getTarget(startCol, startRow, minSteps, colDirection, rowDirection): GridPoint | null {
		minSteps = Math.round(minSteps);

		let colTarget = startCol + minSteps * colDirection;
		let rowTarget = startRow + minSteps * rowDirection;
		let canCheck = true;

		while (canCheck) {
			if (this.hasPoint(colTarget, rowTarget)) {
				return {
					col: colTarget,
					row: rowTarget
				};
			}

			colTarget += colDirection;
			rowTarget += rowDirection;

			if (colTarget < 0 || colTarget > this.totalCols) {
				canCheck = false;
			}

			if (rowTarget < 0 || rowTarget > this.totalRows) {
				canCheck = false;
			}
		}

		return null;
	}
};
